/**
 * Tisha Smart Search — query expansion, search redirects, fallback alternatives.
 * Loads config from /tisha-search-config.json (Shopify asset_url) injected via header.liquid.
 * Exposes window.TishaSmartSearch with: ready(), expandQuery(q), checkRedirect(q),
 * fetchFallbackProducts(q, limit), renderFallbackHTML(q, products, label).
 *
 * Used by header.liquid (predictive search + form submit) and main-search.liquid
 * (no-results section).
 */
(function () {
  'use strict';

  var configPromise = null;
  var configCache = null;

  function loadConfig() {
    if (configCache) return Promise.resolve(configCache);
    if (configPromise) return configPromise;
    var url = window.TishaSearchConfigUrl || '/tisha-search-config.json';
    configPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cfg) {
        configCache = cfg || { phrase_synonyms: {}, word_synonyms: {}, redirects: [], fallbacks: [] };
        return configCache;
      })
      .catch(function () {
        configCache = { phrase_synonyms: {}, word_synonyms: {}, redirects: [], fallbacks: [] };
        return configCache;
      });
    return configPromise;
  }

  function normalize(q) {
    return (q || '').toString().trim().toLowerCase();
  }

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Expand query: phrase synonyms first (multi-word), then word synonyms (per-token).
   * Returns canonical form. Idempotent for already-canonical input.
   */
  function expandQueryWith(cfg, q) {
    if (!q) return q;
    var result = normalize(q);

    // Phase 1: phrase replacements (longest first to avoid partial overlap)
    var phrases = Object.keys(cfg.phrase_synonyms || {}).sort(function (a, b) {
      return b.length - a.length;
    });
    phrases.forEach(function (phrase) {
      var re = new RegExp('\\b' + escapeRegExp(phrase) + '\\b', 'gi');
      result = result.replace(re, cfg.phrase_synonyms[phrase]);
    });

    // Phase 2: word replacements (token-level)
    var words = result.split(/\s+/).filter(Boolean);
    var mapped = words.map(function (w) {
      var key = w.toLowerCase();
      return (cfg.word_synonyms && cfg.word_synonyms[key]) || w;
    });

    // Dedupe consecutive duplicates created by mapping
    var deduped = mapped.filter(function (w, i) {
      return i === 0 || w !== mapped[i - 1];
    });

    return deduped.join(' ').trim();
  }

  /**
   * Check if normalized query (or expanded) matches a search-term redirect.
   * Returns target URL or null.
   */
  function checkRedirectWith(cfg, q) {
    var info = getRedirectInfoWith(cfg, q);
    return info ? info.url : null;
  }

  /**
   * Same as checkRedirect but returns full info: { url, title, match }.
   * title is derived from the URL's last meaningful segment.
   */
  function getRedirectInfoWith(cfg, q) {
    if (!q || !Array.isArray(cfg.redirects)) return null;
    var norm = normalize(q);
    var expanded = expandQueryWith(cfg, q);

    for (var i = 0; i < cfg.redirects.length; i++) {
      var r = cfg.redirects[i];
      if (!r || !Array.isArray(r.match)) continue;
      for (var j = 0; j < r.match.length; j++) {
        var m = r.match[j].toLowerCase();
        if (norm === m || expanded === m) {
          return { url: r.url, title: r.title || titleFromUrl(r.url), matched: m };
        }
      }
    }
    return null;
  }

  function titleFromUrl(url) {
    if (!url) return '';
    // Strip query/hash, take last segment
    var path = url.split(/[?#]/)[0];
    var parts = path.split('/').filter(Boolean);
    var last = parts[parts.length - 1] || '';
    return last
      .replace(/-/g, ' ')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); })
      .replace(/\bB2b\b/g, 'B2B')
      .replace(/\bFm\b/g, 'FM')
      .replace(/\bAi\b/g, 'AI')
      .replace(/\bUk\b/g, 'UK')
      .replace(/\bGdpr\b/g, 'GDPR')
      .replace(/\bFaq\b/g, 'FAQ');
  }

  /**
   * Pick best-fit fallback rule for a query (most specific match wins).
   */
  function pickFallback(cfg, q) {
    if (!Array.isArray(cfg.fallbacks)) return null;
    var expanded = expandQueryWith(cfg, q);
    var best = null;
    var bestLen = 0;

    cfg.fallbacks.forEach(function (f) {
      if (f.default || !Array.isArray(f.match)) return;
      f.match.forEach(function (m) {
        var ml = m.toLowerCase();
        if (expanded.indexOf(ml) !== -1 && ml.length > bestLen) {
          best = f;
          bestLen = ml.length;
        }
      });
    });

    if (best) return best;
    return cfg.fallbacks.find(function (f) { return f.default; }) || null;
  }

  /**
   * Fetch fallback products for a query.
   * Strategy: 1) try expanded query against /search/suggest.json — if hits, return those.
   *           2) else fall back to /collections/{handle}/products.json from rule.
   */
  function fetchFallbackProductsWith(cfg, q, limit) {
    limit = limit || 6;
    var expanded = expandQueryWith(cfg, q);

    // Strategy A: re-search with canonical query (handles typo cases).
    var retryUrl = '/search/suggest.json?q=' + encodeURIComponent(expanded) +
      '&resources[type]=product&resources[limit]=' + limit +
      '&resources[options][unavailable_products]=last';

    return fetch(retryUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var products = data && data.resources && data.resources.results &&
          data.resources.results.products ? data.resources.results.products : [];
        if (products.length > 0 && expanded !== normalize(q)) {
          return { products: products, source: 'expanded', label: 'Did you mean "' + expanded + '"?' };
        }
        return null;
      })
      .catch(function () { return null; })
      .then(function (result) {
        if (result) return result;
        // Strategy B: load from fallback collection.
        var rule = pickFallback(cfg, q);
        if (!rule) return { products: [], source: 'none', label: '' };

        var collUrl = '/collections/' + rule.collection + '/products.json?limit=' + limit;
        return fetch(collUrl, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            var products = (data && data.products) ? data.products : [];
            // Normalize fields to match /search/suggest.json shape consumed by render code.
            var normalized = products.map(function (p) {
              var img = p.images && p.images[0] ? p.images[0].src : '';
              var price = p.variants && p.variants[0] ? p.variants[0].price : '';
              return {
                title: p.title,
                url: '/products/' + p.handle,
                image: img,
                price: price ? formatMoney(price) : ''
              };
            });
            return {
              products: normalized,
              source: 'collection',
              label: rule.label || 'You may also like',
              collection_url: '/collections/' + rule.collection
            };
          })
          .catch(function () { return { products: [], source: 'none', label: '' }; });
      });
  }

  function formatMoney(amount) {
    if (typeof amount !== 'string' && typeof amount !== 'number') return '';
    var num = parseFloat(amount);
    if (isNaN(num)) return '';
    var sym = window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'GBP' ? '£' : '£';
    return sym + num.toFixed(2);
  }

  /**
   * Render alternatives HTML for predictive dropdown OR /search page no-results.
   * style: 'predictive' (compact rows) | 'page' (grid).
   */
  function renderFallbackHTML(originalQ, result, style) {
    if (!result || !result.products || result.products.length === 0) {
      return '<p class="ts-search-empty">We couldn’t find anything matching “' +
        escapeHtml(originalQ) + '”. <a href="/pages/contact">Contact us</a> and we’ll source it for you.</p>';
    }

    var label = result.label || 'You may also like';
    var html = '';

    if (style === 'page') {
      html += '<div class="ts-fallback ts-fallback--page">';
      html += '<h3 class="ts-fallback__heading">' + escapeHtml(label) + '</h3>';
      if (result.source === 'collection') {
        html += '<p class="ts-fallback__hint">Nothing matched “' + escapeHtml(originalQ) +
          '”, but these are popular alternatives:</p>';
      }
      html += '<div class="ts-fallback__grid">';
      result.products.forEach(function (p) {
        html += productCardHTML(p, 'page');
      });
      html += '</div>';
      if (result.collection_url) {
        html += '<a href="' + result.collection_url + '" class="ts-fallback__more">Browse all →</a>';
      }
      html += '</div>';
    } else {
      // predictive style — compact list (matches existing tisha-predictive__item)
      html += '<div class="tisha-predictive__hint">' + escapeHtml(label) + '</div>';
      result.products.forEach(function (p) {
        html += productCardHTML(p, 'predictive');
      });
    }
    return html;
  }

  function productCardHTML(p, style) {
    var img = p.image ? '<img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '';
    if (style === 'page') {
      return '<a href="' + p.url + '" class="ts-fallback__item">' +
        '<div class="ts-fallback__img">' + img + '</div>' +
        '<div class="ts-fallback__info">' +
        '<span class="ts-fallback__name">' + escapeHtml(p.title) + '</span>' +
        '<span class="ts-fallback__price">' + (p.price || '') + '</span>' +
        '</div></a>';
    }
    return '<a href="' + p.url + '" class="tisha-predictive__item">' +
      '<div class="tisha-predictive__img">' + img + '</div>' +
      '<div class="tisha-predictive__info">' +
      '<span class="tisha-predictive__name">' + escapeHtml(p.title) + '</span>' +
      '<span class="tisha-predictive__price">' + (p.price || '') + '</span>' +
      '</div></a>';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Render a prominent "page suggestion" card for the predictive dropdown.
   * Used when query matches a search-term redirect — makes it obvious you
   * can open the page rather than browse products.
   */
  function renderRedirectSuggestionHTML(q, info) {
    if (!info || !info.url) return '';
    return '<a href="' + info.url + '" class="tisha-predictive__page-link">' +
      '<div class="tisha-predictive__page-icon">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>' +
      '</div>' +
      '<div class="tisha-predictive__page-text">' +
      '<span class="tisha-predictive__page-label">Open page</span>' +
      '<span class="tisha-predictive__page-name">' + escapeHtml(info.title) + ' &rarr;</span>' +
      '</div>' +
      '</a>';
  }

  // Public API
  window.TishaSmartSearch = {
    ready: loadConfig,
    expandQuery: function (q) { return loadConfig().then(function (cfg) { return expandQueryWith(cfg, q); }); },
    expandQuerySync: function (q) { return configCache ? expandQueryWith(configCache, q) : q; },
    checkRedirect: function (q) { return loadConfig().then(function (cfg) { return checkRedirectWith(cfg, q); }); },
    checkRedirectSync: function (q) { return configCache ? checkRedirectWith(configCache, q) : null; },
    getRedirectInfoSync: function (q) { return configCache ? getRedirectInfoWith(configCache, q) : null; },
    fetchFallbackProducts: function (q, limit) { return loadConfig().then(function (cfg) { return fetchFallbackProductsWith(cfg, q, limit); }); },
    renderFallbackHTML: renderFallbackHTML,
    renderRedirectSuggestionHTML: renderRedirectSuggestionHTML,
    _configCache: function () { return configCache; }
  };

  // Preload config so sync methods are ready by first user input.
  loadConfig();
})();
